/**
 * TypeScript types for Deployment API
 * Based on GraphQL schema
 */

// ============================================================================
// Enums
// ============================================================================

export enum DeploymentStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum DiffLineType {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
  UNCHANGED = 'UNCHANGED',
}

// ============================================================================
// Core Types
// ============================================================================

export interface Organization {
  id: string;
  orgName: string;
}

export interface MetadataRetrieval {
  id: string;
  status: string;
}

export interface MetadataItem {
  id: string;
  metadataType: string;
  fullName: string;
  fileName: string | null;
  content: string | null;
  sha256Hash: string | null;
  apiVersion?: number;
  status?: string;
  isValid?: boolean;
  createdDate?: string;
  lastModifiedDate?: string;
  createdByName?: string;
  lastModifiedByName?: string;
  toolingEnriched: boolean;
  metadataRetrieval: MetadataRetrieval;
  organization: Organization;
  insertedAt: string;
  updatedAt: string;
}

export interface MetadataDeployment {
  id: string;
  status: DeploymentStatus;
  asyncProcessId: string | null;
  deployOptions: string; // JSON string
  resultDetails: string | null; // JSON string
  errorMessage: string | null;
  organization: Organization;
  metadataItems: MetadataItem[];
  startedAt: string | null;
  completedAt: string | null;
  insertedAt: string;
  updatedAt: string;
}

// ============================================================================
// Relay Connection Types (for pagination)
// ============================================================================

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

export interface MetadataItemEdge {
  node: MetadataItem;
  cursor: string;
}

export interface MetadataItemConnection {
  edges: MetadataItemEdge[];
  pageInfo: PageInfo;
}

// ============================================================================
// Query Response Types
// ============================================================================

export interface EditedMetadataItemsResponse {
  editedMetadataItems: MetadataItemConnection;
}

export interface MetadataDeploymentsResponse {
  metadataDeployments: MetadataDeployment[];
}

export interface MetadataDeploymentResponse {
  metadataDeployment: MetadataDeployment | null;
}

// ============================================================================
// Diff Types
// ============================================================================

export interface DiffLine {
  type: DiffLineType;
  content: string;
  oldLineNumber: number | null;
  newLineNumber: number | null;
}

export interface MetadataItemUpdateCheckResult {
  hasChanges: boolean;
  localContent: string | null;
  remoteContent: string | null;
  metadataType: string;
  fullName: string;
  diff: DiffLine[] | null;
}

export interface CheckMetadataItemUpdateResponse {
  checkMetadataItemUpdate: MetadataItemUpdateCheckResult;
}

// ============================================================================
// Mutation Input Types
// ============================================================================

export interface UpdateMetadataItemInput {
  id: string;
  content: string;
}

export interface DeployOptionsInput {
  testOnly?: boolean;
  checkOnly?: boolean;
  singlePackage?: boolean;
  rollbackOnError?: boolean;
  runAllTests?: boolean;
  ignoreWarnings?: boolean;
}

export interface DeployMetadataInput {
  organizationId: string;
  metadataItemIds: string[];
  commitMessage?: string;
  apiVersion?: string;
  deployOptions?: DeployOptionsInput;
}

export interface MetadataFileInput {
  filename: string;
  content: string;
}

export interface UploadMetadataFilesInput {
  organizationId: string;
  retrievalId?: string;
  updateExisting?: boolean;
  files: MetadataFileInput[];
}

// ============================================================================
// Mutation Response Types
// ============================================================================

export interface UpdateMetadataItemResponse {
  updateMetadataItem: MetadataItem;
}

export interface DeployMetadataResponse {
  deployMetadata: MetadataDeployment;
}

export interface UploadError {
  index: number;
  filename: string | null;
  error: string;
}

export interface UploadMetadataFilesResult {
  created: MetadataItem[];
  updated: MetadataItem[];
  errors: UploadError[];
}

export interface UploadMetadataFilesResponse {
  uploadMetadataFiles: UploadMetadataFilesResult;
}

// ============================================================================
// Query Variables
// ============================================================================

export interface EditedMetadataItemsVariables {
  organizationId: string;
  retrievalId: string;
  isEdited?: boolean;
  first?: number;
  after?: string;
  last?: number;
  before?: string;
}

export interface MetadataDeploymentsVariables {
  organizationId: string;
  status?: DeploymentStatus;
}

export interface MetadataDeploymentVariables {
  id?: string;
  organizationId?: string;
  status?: DeploymentStatus;
}

export interface CheckMetadataItemUpdateVariables {
  metadataItemId: string;
  organizationId?: string;
}

